import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  Megaphone, ArrowLeft, RefreshCw, PlugZap, CheckCircle2, XCircle,
  Sparkles, Bot, Send, TrendingUp, TrendingDown, PauseCircle, Eye
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const uid = () => (crypto?.randomUUID ? crypto.randomUUID() : `id-${Date.now()}-${Math.random()}`);
const chf = (v) => (v === null || v === undefined ? '–' : `CHF ${Number(v).toFixed(2)}`);

const ConnBadge = ({ ok, testid }) => (
  ok
    ? <Badge data-testid={testid} className="bg-emerald-600 hover:bg-emerald-600"><CheckCircle2 className="w-3 h-3 mr-1" />Verbunden</Badge>
    : <Badge data-testid={testid} variant="secondary"><XCircle className="w-3 h-3 mr-1" />Nicht verbunden</Badge>
);

const Field = ({ id, label, type = 'text', value, onChange, placeholder, hint }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id}>{label}</Label>
    <Input id={id} data-testid={id} type={type} value={value || ''} placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)} />
    {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
  </div>
);

export default function AdsSettingsPage() {
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState('');
  const [datePreset, setDatePreset] = useState('last_30d');
  const [googleDays, setGoogleDays] = useState(30);
  const [metaCampaigns, setMetaCampaigns] = useState(null);
  const [googleCampaigns, setGoogleCampaigns] = useState(null);
  const [recs, setRecs] = useState(null);
  const [analysis, setAnalysis] = useState('');
  const [runs, setRuns] = useState([]);

  const set = (k) => (v) => setForm((p) => ({ ...p, [k]: v }));

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/ads/settings`, authHeader());
      setS(res.data);
      setForm(res.data);
    } catch (e) {
      toast.error('Einstellungen konnten nicht geladen werden');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        meta_access_token: form.meta_access_token, meta_ad_account_id: form.meta_ad_account_id,
        meta_business_id: form.meta_business_id, meta_api_version: form.meta_api_version,
        meta_pixel_id: form.meta_pixel_id, meta_capi_token: form.meta_capi_token,
        capi_enabled: !!form.capi_enabled, capi_default_source_url: form.capi_default_source_url,
        capi_test_event_code: form.capi_test_event_code,
        google_developer_token: form.google_developer_token, google_customer_id: form.google_customer_id,
        google_login_customer_id: form.google_login_customer_id, google_client_id: form.google_client_id,
        google_client_secret: form.google_client_secret, google_refresh_token: form.google_refresh_token,
        google_api_version: form.google_api_version,
        target_cost_per_lead: form.target_cost_per_lead === '' ? undefined : Number(form.target_cost_per_lead),
        budget_step_percent: form.budget_step_percent === '' ? undefined : Number(form.budget_step_percent),
        autopilot_mode: form.autopilot_mode,
        autopilot_max_daily_budget: form.autopilot_max_daily_budget === '' ? undefined : Number(form.autopilot_max_daily_budget),
      };
      const res = await axios.put(`${API}/ads/settings`, payload, authHeader());
      setS(res.data);
      setForm((p) => ({ ...p, ...res.data, meta_access_token: '', meta_capi_token: '', google_developer_token: '', google_client_secret: '', google_refresh_token: '' }));
      toast.success('Einstellungen gespeichert');
    } catch (e) {
      toast.error('Speichern fehlgeschlagen');
    } finally { setSaving(false); }
  };

  const testMeta = async () => {
    setBusy('meta'); try {
      const r = await axios.post(`${API}/ads/meta/test`, {}, authHeader());
      if (r.data.ok) toast.success(`Meta ok: ${r.data.account_name || ''} (${r.data.currency || ''})`);
      else toast.error(r.data.message || r.data.error?.meta_error?.message || 'Meta-Verbindung fehlgeschlagen');
    } catch { toast.error('Meta-Test fehlgeschlagen'); } finally { setBusy(''); }
  };
  const testGoogle = async () => {
    setBusy('google'); try {
      const r = await axios.post(`${API}/ads/google/test`, {}, authHeader());
      if (r.data.ok) toast.success(`Google ok: ${r.data.account_name || ''} (${r.data.currency || ''})`);
      else toast.error(r.data.message || r.data.error?.error || 'Google-Verbindung fehlgeschlagen');
    } catch { toast.error('Google-Test fehlgeschlagen'); } finally { setBusy(''); }
  };
  const testCapi = async () => {
    setBusy('capi'); try {
      const r = await axios.post(`${API}/ads/capi/test-event`, { event_name: 'Lead' }, authHeader());
      const st = r.data?.result?.status;
      if (st === 'preview_blocked') toast.success('CAPI-Testevent (Preview – kein echter Versand) protokolliert');
      else if (st === 'sent') toast.success('CAPI-Testevent an Meta gesendet');
      else if (st === 'not_configured') toast.error('CAPI ist nicht aktiviert oder Pixel/Token fehlt – zuerst speichern.');
      else toast.error(`CAPI-Test fehlgeschlagen (${st || 'unbekannt'})`);
    } catch { toast.error('CAPI-Test fehlgeschlagen'); } finally { setBusy(''); }
  };

  const loadMetaCampaigns = async () => {
    setBusy('meta-c'); try {
      const r = await axios.get(`${API}/ads/meta/campaigns?date_preset=${datePreset}`, authHeader());
      setMetaCampaigns(r.data);
      if (!r.data.connected) toast.info('Meta ist nicht verbunden');
    } catch { toast.error('Meta-Kampagnen konnten nicht geladen werden'); } finally { setBusy(''); }
  };
  const loadGoogleCampaigns = async () => {
    setBusy('google-c'); try {
      const r = await axios.get(`${API}/ads/google/campaigns?days=${googleDays}`, authHeader());
      setGoogleCampaigns(r.data);
      if (!r.data.connected) toast.info('Google ist nicht verbunden');
    } catch { toast.error('Google-Kampagnen konnten nicht geladen werden'); } finally { setBusy(''); }
  };

  const loadRecs = async () => {
    setBusy('recs'); try {
      const r = await axios.get(`${API}/ads/recommendations?date_preset=${datePreset}`, authHeader());
      setRecs(r.data);
      if (!r.data.connected) toast.info('Meta ist nicht verbunden');
    } catch { toast.error('Empfehlungen konnten nicht geladen werden'); } finally { setBusy(''); }
  };
  const runAiAnalysis = async () => {
    setBusy('ai'); try {
      const r = await axios.get(`${API}/ads/ai-analysis?date_preset=${datePreset}`, authHeader());
      setRecs({ connected: r.data.connected, recommendations: r.data.recommendations || [] });
      setAnalysis(r.data.analysis || (r.data.connected ? 'Keine Analyse verfügbar.' : ''));
      if (!r.data.connected) toast.info('Meta ist nicht verbunden');
    } catch { toast.error('KI-Analyse fehlgeschlagen'); } finally { setBusy(''); }
  };

  const applyRec = async (rec) => {
    const change = rec.proposed_change || {};
    const summary = change.status === 'PAUSED' ? 'Kampagne pausieren'
      : change.daily_budget_chf !== undefined ? `Tagesbudget auf ${chf(change.daily_budget_chf)} setzen` : 'Änderung anwenden';
    if (!window.confirm(`${rec.campaign_name}\n\n${summary}?`)) return;
    try {
      await axios.post(`${API}/ads/meta/campaigns/${rec.campaign_id}/change`, {
        ...change, approval_id: uid(), recommendation_id: rec.id,
      }, authHeader());
      toast.success('Änderung angewendet');
      loadRecs();
    } catch (e) {
      toast.error('Anwenden fehlgeschlagen: ' + (e?.response?.data?.detail?.meta_error?.message || 'Fehler'));
    }
  };

  const loadRuns = async () => {
    try { const r = await axios.get(`${API}/ads/autopilot/runs?limit=10`, authHeader()); setRuns(r.data.runs || []); } catch { /* ignore */ }
  };
  const runAutopilotNow = async () => {
    setBusy('autopilot'); try {
      const r = await axios.post(`${API}/ads/autopilot/run-now`, {}, authHeader());
      if (r.data.status === 'disabled') toast.info('Autopilot ist auf "Nur Vorschläge" – aktiviere "Automatisch" zum Ausführen.');
      else if (r.data.status === 'meta_not_connected') toast.info('Meta ist nicht verbunden.');
      else toast.success(`Autopilot: ${r.data.applied_count} angewendet, ${r.data.skipped_count} übersprungen`);
      loadRuns();
    } catch { toast.error('Autopilot-Lauf fehlgeschlagen'); } finally { setBusy(''); }
  };

  useEffect(() => { loadRuns(); }, []);

  if (!s) return <div className="p-8 text-muted-foreground">Lädt…</div>;

  const recIcon = (t) => t === 'increase_budget' ? <TrendingUp className="w-4 h-4 text-emerald-600" />
    : t === 'decrease_budget' ? <TrendingDown className="w-4 h-4 text-amber-600" />
      : t === 'pause' ? <PauseCircle className="w-4 h-4 text-red-600" /> : <Eye className="w-4 h-4 text-slate-500" />;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="ads-settings-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" data-testid="ads-back-btn" onClick={() => navigate(-1)}><ArrowLeft className="w-5 h-5" /></Button>
        <div className="flex items-center gap-2">
          <Megaphone className="w-7 h-7 text-amber-500" />
          <div>
            <h1 className="text-2xl font-bold">Werbung (Ads)</h1>
            <p className="text-sm text-muted-foreground">Meta &amp; Google Ads steuern, KI-Empfehlungen und Conversions API</p>
          </div>
        </div>
        <div className="ml-auto flex gap-2">
          <ConnBadge ok={s.meta_connected} testid="meta-conn-badge" />
          <ConnBadge ok={s.google_connected} testid="google-conn-badge" />
        </div>
      </div>

      <Tabs defaultValue="verbindungen">
        <TabsList data-testid="ads-tabs">
          <TabsTrigger value="verbindungen" data-testid="tab-verbindungen">Verbindungen</TabsTrigger>
          <TabsTrigger value="kampagnen" data-testid="tab-kampagnen">Kampagnen</TabsTrigger>
          <TabsTrigger value="empfehlungen" data-testid="tab-empfehlungen">KI-Empfehlungen</TabsTrigger>
          <TabsTrigger value="autopilot" data-testid="tab-autopilot">Autopilot</TabsTrigger>
        </TabsList>

        {/* VERBINDUNGEN */}
        <TabsContent value="verbindungen" className="space-y-6 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2"><PlugZap className="w-5 h-5" /> Meta Marketing (Facebook/Instagram)</span>
                <ConnBadge ok={s.meta_connected} testid="meta-conn-badge-2" />
              </CardTitle>
              <CardDescription>Zugangsdaten aus dem Meta Business Manager. Wird sicher gespeichert und maskiert angezeigt.</CardDescription>
            </CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-4">
              <Field id="meta-access-token" label="Access Token" type="password" value={form.meta_access_token}
                onChange={set('meta_access_token')} placeholder={s.meta_access_token_masked || 'System-User Token'} />
              <Field id="meta-ad-account-id" label="Werbekonto-ID (act_…)" value={form.meta_ad_account_id} onChange={set('meta_ad_account_id')} placeholder="act_1234567890" />
              <Field id="meta-business-id" label="Business-ID (optional)" value={form.meta_business_id} onChange={set('meta_business_id')} />
              <Field id="meta-api-version" label="API-Version" value={form.meta_api_version} onChange={set('meta_api_version')} placeholder="v26.0" />
              <div className="md:col-span-2 flex gap-2">
                <Button variant="outline" data-testid="meta-test-btn" onClick={testMeta} disabled={busy === 'meta'}>
                  {busy === 'meta' ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <PlugZap className="w-4 h-4 mr-2" />}Verbindung testen
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2"><PlugZap className="w-5 h-5" /> Google Ads</span>
                <ConnBadge ok={s.google_connected} testid="google-conn-badge-2" />
              </CardTitle>
              <CardDescription>OAuth-Zugangsdaten (Developer-Token + Refresh-Token). Kundennummer ohne Bindestriche.</CardDescription>
            </CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-4">
              <Field id="google-developer-token" label="Developer-Token" type="password" value={form.google_developer_token} onChange={set('google_developer_token')} placeholder={s.google_developer_token_masked || ''} />
              <Field id="google-customer-id" label="Ziel-Kundennummer" value={form.google_customer_id} onChange={set('google_customer_id')} placeholder="1234567890" />
              <Field id="google-login-customer-id" label="Login/Manager-Kundennummer (optional)" value={form.google_login_customer_id} onChange={set('google_login_customer_id')} />
              <Field id="google-client-id" label="OAuth Client-ID" value={form.google_client_id} onChange={set('google_client_id')} />
              <Field id="google-client-secret" label="OAuth Client-Secret" type="password" value={form.google_client_secret} onChange={set('google_client_secret')} placeholder={s.google_client_secret_set ? '•••• gesetzt' : ''} />
              <Field id="google-refresh-token" label="Refresh-Token" type="password" value={form.google_refresh_token} onChange={set('google_refresh_token')} placeholder={s.google_refresh_token_set ? '•••• gesetzt' : ''} />
              <Field id="google-api-version" label="API-Version" value={form.google_api_version} onChange={set('google_api_version')} placeholder="v25" />
              <div className="md:col-span-2 flex gap-2">
                <Button variant="outline" data-testid="google-test-btn" onClick={testGoogle} disabled={busy === 'google'}>
                  {busy === 'google' ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <PlugZap className="w-4 h-4 mr-2" />}Verbindung testen
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2"><Send className="w-5 h-5" /> Meta Conversions API (CAPI)</span>
                <ConnBadge ok={s.capi_connected && s.capi_enabled} testid="capi-conn-badge" />
              </CardTitle>
              <CardDescription>Meldet echte Erfolge (Lead, Kauf, Registrierung, Terminbuchung) serverseitig an Meta – für bessere Werbe-Optimierung.</CardDescription>
            </CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-4">
              <Field id="meta-pixel-id" label="Pixel-ID" value={form.meta_pixel_id} onChange={set('meta_pixel_id')} placeholder="123456789012345" />
              <Field id="meta-capi-token" label="CAPI Access Token" type="password" value={form.meta_capi_token} onChange={set('meta_capi_token')} placeholder={s.meta_capi_token_masked || ''} />
              <Field id="capi-default-source-url" label="Standard-Quell-URL" value={form.capi_default_source_url} onChange={set('capi_default_source_url')} placeholder="https://strongfit.ch" hint="Wird bei Website-Events ohne Browser-Kontext verwendet." />
              <Field id="capi-test-event-code" label="Test-Event-Code (optional)" value={form.capi_test_event_code} onChange={set('capi_test_event_code')} placeholder="TEST12345" hint="Nur zum Testen in Meta Events Manager. Für Produktion leer lassen." />
              <div className="flex items-center gap-3 md:col-span-2">
                <Switch id="capi-enabled" data-testid="capi-enabled-switch" checked={!!form.capi_enabled} onCheckedChange={set('capi_enabled')} />
                <Label htmlFor="capi-enabled">Conversions API aktiviert</Label>
              </div>
              <div className="md:col-span-2 flex gap-2">
                <Button variant="outline" data-testid="capi-test-btn" onClick={testCapi} disabled={busy === 'capi'}>
                  {busy === 'capi' ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}Testevent senden
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button data-testid="ads-save-btn" onClick={save} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-black">
              {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : null}Alle Einstellungen speichern
            </Button>
          </div>
        </TabsContent>

        {/* KAMPAGNEN */}
        <TabsContent value="kampagnen" className="space-y-6 mt-4">
          <div className="flex items-center gap-3">
            <Label>Zeitraum</Label>
            <Select value={datePreset} onValueChange={setDatePreset}>
              <SelectTrigger className="w-44" data-testid="date-preset-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="today">Heute</SelectItem>
                <SelectItem value="yesterday">Gestern</SelectItem>
                <SelectItem value="last_7d">Letzte 7 Tage</SelectItem>
                <SelectItem value="last_14d">Letzte 14 Tage</SelectItem>
                <SelectItem value="last_30d">Letzte 30 Tage</SelectItem>
                <SelectItem value="this_month">Dieser Monat</SelectItem>
                <SelectItem value="last_month">Letzter Monat</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" data-testid="load-meta-campaigns-btn" onClick={loadMetaCampaigns} disabled={busy === 'meta-c'}>
              {busy === 'meta-c' ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}Meta laden
            </Button>
          </div>

          <Card>
            <CardHeader><CardTitle>Meta-Kampagnen</CardTitle></CardHeader>
            <CardContent>
              {!metaCampaigns ? <p className="text-sm text-muted-foreground">Noch nicht geladen.</p>
                : !metaCampaigns.connected ? <p className="text-sm text-muted-foreground">Meta ist nicht verbunden. Bitte unter „Verbindungen" eintragen.</p>
                  : metaCampaigns.campaigns.length === 0 ? <p className="text-sm text-muted-foreground">Keine Kampagnen im Zeitraum.</p>
                    : (
                      <Table data-testid="meta-campaigns-table">
                        <TableHeader><TableRow>
                          <TableHead>Kampagne</TableHead><TableHead>Status</TableHead><TableHead>Ausgaben</TableHead>
                          <TableHead>Leads</TableHead><TableHead>CHF/Lead</TableHead><TableHead>ROAS</TableHead><TableHead>Tagesbudget</TableHead>
                        </TableRow></TableHeader>
                        <TableBody>
                          {metaCampaigns.campaigns.map((c) => (
                            <TableRow key={c.id} data-testid={`meta-campaign-row-${c.id}`}>
                              <TableCell className="font-medium">{c.name}</TableCell>
                              <TableCell><Badge variant={c.effective_status === 'ACTIVE' ? 'default' : 'secondary'}>{c.effective_status || c.status}</Badge></TableCell>
                              <TableCell>{chf(c.spend)}</TableCell>
                              <TableCell>{c.leads ?? '–'}</TableCell>
                              <TableCell>{chf(c.cost_per_lead)}</TableCell>
                              <TableCell>{c.roas ?? '–'}</TableCell>
                              <TableCell>{chf(c.daily_budget_chf)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
            </CardContent>
          </Card>

          <div className="flex items-center gap-3">
            <Label>Google-Tage</Label>
            <Select value={String(googleDays)} onValueChange={(v) => setGoogleDays(Number(v))}>
              <SelectTrigger className="w-44" data-testid="google-days-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Letzte 7 Tage</SelectItem>
                <SelectItem value="14">Letzte 14 Tage</SelectItem>
                <SelectItem value="30">Letzte 30 Tage</SelectItem>
                <SelectItem value="90">Letzte 90 Tage</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" data-testid="load-google-campaigns-btn" onClick={loadGoogleCampaigns} disabled={busy === 'google-c'}>
              {busy === 'google-c' ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}Google laden
            </Button>
          </div>

          <Card>
            <CardHeader><CardTitle>Google-Kampagnen</CardTitle></CardHeader>
            <CardContent>
              {!googleCampaigns ? <p className="text-sm text-muted-foreground">Noch nicht geladen.</p>
                : !googleCampaigns.connected ? <p className="text-sm text-muted-foreground">Google ist nicht verbunden. Bitte unter „Verbindungen" eintragen.</p>
                  : googleCampaigns.campaigns.length === 0 ? <p className="text-sm text-muted-foreground">Keine Kampagnen im Zeitraum.</p>
                    : (
                      <Table data-testid="google-campaigns-table">
                        <TableHeader><TableRow>
                          <TableHead>Kampagne</TableHead><TableHead>Status</TableHead><TableHead>Ausgaben</TableHead>
                          <TableHead>Klicks</TableHead><TableHead>Conversions</TableHead><TableHead>CHF/Conv.</TableHead><TableHead>Tagesbudget</TableHead>
                        </TableRow></TableHeader>
                        <TableBody>
                          {googleCampaigns.campaigns.map((c) => (
                            <TableRow key={c.id} data-testid={`google-campaign-row-${c.id}`}>
                              <TableCell className="font-medium">{c.name}</TableCell>
                              <TableCell><Badge variant={c.status === 'ENABLED' ? 'default' : 'secondary'}>{c.status}</Badge></TableCell>
                              <TableCell>{chf(c.spend)}</TableCell>
                              <TableCell>{c.clicks}</TableCell>
                              <TableCell>{c.conversions}</TableCell>
                              <TableCell>{chf(c.cost_per_conversion)}</TableCell>
                              <TableCell>{chf(c.daily_budget_chf)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* EMPFEHLUNGEN */}
        <TabsContent value="empfehlungen" className="space-y-6 mt-4">
          <div className="flex items-center gap-3">
            <Button variant="outline" data-testid="load-recs-btn" onClick={loadRecs} disabled={busy === 'recs'}>
              {busy === 'recs' ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Bot className="w-4 h-4 mr-2" />}Regel-Empfehlungen
            </Button>
            <Button data-testid="ai-analysis-btn" onClick={runAiAnalysis} disabled={busy === 'ai'} className="bg-amber-500 hover:bg-amber-600 text-black">
              {busy === 'ai' ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}KI-Analyse
            </Button>
          </div>

          {analysis && (
            <Card data-testid="ai-analysis-card">
              <CardHeader><CardTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-amber-500" /> KI-Analyse</CardTitle></CardHeader>
              <CardContent><p className="text-sm whitespace-pre-wrap">{analysis}</p></CardContent>
            </Card>
          )}

          <Card>
            <CardHeader><CardTitle>Empfehlungen</CardTitle><CardDescription>Der Bot schlägt vor – du bestätigst jede Änderung.</CardDescription></CardHeader>
            <CardContent className="space-y-3">
              {!recs ? <p className="text-sm text-muted-foreground">Noch nicht geladen.</p>
                : !recs.connected ? <p className="text-sm text-muted-foreground">Meta ist nicht verbunden.</p>
                  : (recs.recommendations || []).length === 0 ? <p className="text-sm text-muted-foreground">Keine Empfehlungen – alles im grünen Bereich.</p>
                    : recs.recommendations.map((r) => (
                      <div key={r.id} data-testid={`rec-row-${r.campaign_id}`} className="flex items-center gap-3 border rounded-lg p-3">
                        {recIcon(r.type)}
                        <div className="flex-1">
                          <div className="font-medium">{r.campaign_name}</div>
                          <div className="text-sm text-muted-foreground">{r.reason}</div>
                        </div>
                        {r.proposed_change && Object.keys(r.proposed_change).length > 0 && (
                          <Button size="sm" data-testid={`rec-apply-${r.campaign_id}`} onClick={() => applyRec(r)}>Anwenden</Button>
                        )}
                      </div>
                    ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* AUTOPILOT */}
        <TabsContent value="autopilot" className="space-y-6 mt-4">
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><Bot className="w-5 h-5" /> Autopilot &amp; Bot-Ziele</CardTitle>
              <CardDescription>Im Modus „Automatisch" wendet der Bot Empfehlungen selbst an – Budgeterhöhungen nur bis zum gesetzten Limit.</CardDescription>
            </CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Modus</Label>
                <Select value={form.autopilot_mode || 'suggest'} onValueChange={set('autopilot_mode')}>
                  <SelectTrigger data-testid="autopilot-mode-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="suggest">Nur Vorschläge</SelectItem>
                    <SelectItem value="auto">Automatisch (mit Limit)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Field id="autopilot-max-daily-budget" label="Max. Tagesbudget pro Kampagne (CHF)" type="number" value={form.autopilot_max_daily_budget} onChange={set('autopilot_max_daily_budget')} hint="0 = keine automatischen Budgeterhöhungen." />
              <Field id="target-cost-per-lead" label="Ziel-Kosten pro Lead (CHF)" type="number" value={form.target_cost_per_lead} onChange={set('target_cost_per_lead')} />
              <Field id="budget-step-percent" label="Budget-Schritt (%)" type="number" value={form.budget_step_percent} onChange={set('budget_step_percent')} />
              <div className="md:col-span-2 flex gap-2">
                <Button data-testid="autopilot-save-btn" onClick={save} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-black">Speichern</Button>
                <Button variant="outline" data-testid="autopilot-run-btn" onClick={runAutopilotNow} disabled={busy === 'autopilot'}>
                  {busy === 'autopilot' ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Bot className="w-4 h-4 mr-2" />}Jetzt ausführen
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Letzte Autopilot-Läufe</CardTitle></CardHeader>
            <CardContent>
              {runs.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="autopilot-runs-empty">Noch keine Läufe.</p>
                : (
                  <Table data-testid="autopilot-runs-table">
                    <TableHeader><TableRow><TableHead>Zeit</TableHead><TableHead>Angewendet</TableHead><TableHead>Übersprungen</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {runs.map((r, i) => (
                        <TableRow key={i}>
                          <TableCell>{new Date(r.created_at).toLocaleString('de-CH')}</TableCell>
                          <TableCell>{r.applied_count}</TableCell>
                          <TableCell>{r.skipped_count}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
